import type { DateTime, TimezoneRegion, Utc } from "@tento-chrono";
import type { CalendarEventRead } from "@tento-lona/platform";
import type { EventItemTimed, Option } from "@tento-lona/sheets";
import type { EventsCacheSlots } from "@tento-lona/sheets/platform";

/** What a span read needs of a row's `CalendarEventRead`. */
export type SpanSource = Pick<CalendarEventRead, "timed" | "span" | "generation">;

/**
 * One SDK read per strip bind, sliced per day (F2, sdk-to-dom-7).
 *
 * A strip bind asks for each day's timed events through the bind's query
 * cache. With the bind's whole range known (`bindRange`), the first day asks
 * the SDK for one `span(bindRange, cache, zone)` and every later day of that bind
 * (and its occlusion reads) is sliced from it: one read per row per bind
 * instead of one per day. A span is keyed by the bind's cache object (so it
 * dies with the bind) and by row, and is dropped when the read's
 * `generation()` moved since it was taken (events changed between queued
 * units of the same bind). Without a cache, or for a range the span does not
 * cover and no bind range to widen to, the read is asked directly, as before.
 * `zone` is the zone the bind's day columns are days of (the viewer's, as the
 * column mapper built `bindRange` in): the span's days are that zone's, so a
 * slice of its all-day lane covers the viewer's columns east of UTC too
 * (sdk-to-dom-55). Without a zone nothing is spanned: the read is asked directly.
 */
export class SpanReads<Row extends object> {
  readonly #spans = new WeakMap<EventsCacheSlots, Map<Row, CalendarEventRead.Span>>();

  /** The timed events of `range` for `row`, from the bind's span when one covers it. */
  timed(
    read: SpanSource,
    row: Row,
    range: DateTime.Range<Utc>,
    cache: Option<EventsCacheSlots>,
    bindRange: Option<DateTime.Range<Utc>>,
    zone: Option<TimezoneRegion>,
  ): readonly EventItemTimed[] {
    if (cache == null) return read.timed(range, null);
    let byRow = this.#spans.get(cache);
    let span = byRow?.get(row) ?? null;
    if (span != null && (!covers(span.range, range) || span.generation !== read.generation())) span = null;
    if (span == null) {
      if (bindRange == null || zone == null || !covers(bindRange, range)) return read.timed(range, cache);
      span = read.span(bindRange, cache, zone);
      if (byRow == null) {
        byRow = new Map();
        this.#spans.set(cache, byRow);
      }
      byRow.set(row, span);
    }
    return span.timed(range);
  }
}

/** Whether `outer` holds `inner`, as instants: either range may be written in any zone. */
function covers(outer: DateTime.Range<string>, inner: DateTime.Range<string>): boolean {
  return outer.start.mse <= inner.start.mse && inner.end.mse <= outer.end.mse;
}
